/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ContactNoteType } from '@/api/entity_types/ContactNote';
import { Api } from '@/api';
import { toast } from 'vue-sonner';
import { useTransformEvent } from './useTransformEvent';
import { HumanizString } from '@/utilities/humaniz_string';
import { useTransformWorkflowScheduledAction } from './useTransformWorkflowScheduledAction';
import RailsUtility from '@/utilities/rails';
import { useTransformScheduledAction } from './useTransformScheduledAction';

export type ActionOption = {
  title: string;
  icon?: string;
  action: { onClick?: () => void; href?: string; emitId?: ActivityPreviewEmitEvent };
};

export type ActivityPreviewEmitEvent =
  | 'delete-interview-clicked'
  | 'cancel-workflow-clicked'
  | 'cancel-scheduled-action-clicked';
export type ActivityContent = { label?: string; richText?: string };

export type ActivityTabCategory = 'emails' | 'messages' | 'calls' | 'notes' | 'timeline';
export type ActivityIconName =
  | 'mail'
  | 'phone-call'
  | 'calendar-days'
  | 'user-round-check'
  | 'message-square'
  | 'workflow'
  | 'clipboard-pen-line'
  | 'git-commit-vertical';

export type ActivityItem = {
  id: number;
  categories: ActivityTabCategory[];
  icon: ActivityIconName;
  verb: string;
  typeLabel: string;
  displayTimestamp: string;
  sortTimestamp: string;
  actor: string;
  label: string;
  content: ActivityContent[];
  buttons?: ActionOption[];
  dropdownOptions?: ActionOption[];
};

export const useActivityTransformer = () => {
  const { transformEvent } = useTransformEvent();
  const { transformWorkflowScheduledAction } = useTransformWorkflowScheduledAction();
  const { transformScheduledAction } = useTransformScheduledAction();

  const transformApplication = (application: any): ActivityItem | null => {
    if (!application) return null;
    const location = application.v2_job?.job_locations?.find((loc: any) => loc.id === application.job_location_id);
    const appliedFor = application.v2_job?.title
      ? `${application.v2_job.title}${location ? ` - ${location.city}, ${location.state}` : ''}`
      : 'General application';

    return {
      id: application.id,
      categories: ['timeline'],
      icon: 'user-round-check',
      verb: 'submitted an application',
      typeLabel: 'Application',
      displayTimestamp: application.created_at,
      sortTimestamp: application.created_at,
      actor: `${application.first_name} ${application.last_name}`,
      label: 'New Application',
      content: [
        {
          label: 'Applied for',
          richText: appliedFor,
        },
      ],
      buttons: application.resume_path
        ? [{ title: 'Download resume', action: { href: application.resume_path } }]
        : undefined,
    };
  };

  const transformEmail = (contactEmail: any): ActivityItem => {
    return {
      id: contactEmail.id,
      categories: ['emails'],
      icon: 'mail',
      verb: 'sent an email',
      typeLabel: 'Email',
      displayTimestamp: contactEmail.created_at,
      sortTimestamp: contactEmail.created_at,
      actor: contactEmail.user.name ?? contactEmail.user.email ?? '',
      label: 'Email',
      content: [{ label: 'Subject', richText: contactEmail.subject }, { richText: contactEmail.body }],
    };
  };

  const transformMessage = (contactMessage: any, contact: any): ActivityItem => {
    const actor = contactMessage.message_type === 'Customer' ? contact : contactMessage.user;
    return {
      id: contactMessage.id,
      categories: ['messages'],
      icon: 'message-square',
      verb: 'sent a message',
      typeLabel: 'Message',
      displayTimestamp: contactMessage.created_at,
      sortTimestamp: contactMessage.created_at,
      actor: actor.name ?? actor.email ?? '',
      label: 'Message',
      content: [
        { label: 'Status', richText: contactMessage.status },
        { richText: contactMessage.body ?? 'No message body available' },
      ],
    };
  };

  const transformNote = (contactNote: any, getContactDetails: () => void): ActivityItem => {
    const deleteNote = async (contactNote: any) => {
      const response = await Api.ContactNotes.deleteContactNote(contactNote.id);
      if (response?.status != 'error') {
        getContactDetails();
        toast.success('Activity deleted', {
          description: 'Your activity was successfully deleted.',
        });
      } else {
        toast.error('Activity delete error', {
          description: response.message,
        });
      }
    };

    const noteType = contactNote.note_type as ContactNoteType;

    const noteLabels = new Map<ContactNoteType, string>([
      ['general', 'Left a Note'],
      ['call', 'Logged a Call'],
      ['email', 'Logged an Email'],
      ['text', 'Logged a Text'],
    ]);

    type NoteDisplayConfig = { categories: ActivityTabCategory[]; icon: ActivityIconName; verb: string };
    const noteCategoryMap: Partial<Record<ContactNoteType, NoteDisplayConfig>> = {
      email: { categories: ['emails', 'notes'], icon: 'mail', verb: 'logged an email' },
      call: { categories: ['calls', 'notes'], icon: 'phone-call', verb: 'logged a call' },
      text: { categories: ['messages', 'notes'], icon: 'message-square', verb: 'logged a message' },
    };
    const noteDefaults: NoteDisplayConfig = { categories: ['notes'], icon: 'clipboard-pen-line', verb: 'left a note' };
    const { categories, icon, verb } = noteCategoryMap[noteType] ?? noteDefaults;

    return {
      id: contactNote.id,
      categories,
      icon,
      verb,
      typeLabel: 'Note',
      displayTimestamp: contactNote.created_at,
      sortTimestamp: contactNote.created_at,
      actor: contactNote.user.name ?? contactNote.user.email ?? '',
      label: noteLabels.get(noteType) ?? 'Note',
      content: [{ richText: contactNote.content ?? 'No note content available' }],
      buttons: [{ title: 'Delete note', action: { onClick: () => deleteNote(contactNote) } }],
    };
  };

  const transformInAppPhoneCall = (inAppPhoneCall: any): ActivityItem => {
    return {
      id: inAppPhoneCall.id,
      categories: ['calls'],
      icon: 'phone-call',
      verb: 'logged a call',
      typeLabel: 'Call',
      displayTimestamp: inAppPhoneCall.created_at,
      sortTimestamp: inAppPhoneCall.created_at,
      actor: inAppPhoneCall.user_caller?.name ?? inAppPhoneCall.user_caller?.email ?? '',
      label: 'In App Call',
      content: [
        { label: 'Call', richText: HumanizString.capitalize(inAppPhoneCall.direction) },
        { richText: inAppPhoneCall.transcript ?? 'No transcript available' },
      ],
    };
  };

  const transformEntityEvents = (event: any): ActivityItem => {
    return {
      id: event.id,
      categories: ['timeline'],
      icon: 'git-commit-vertical',
      verb: 'changed stage',
      typeLabel: 'Timeline',
      label: 'Timeline Event',
      displayTimestamp: event.created_at,
      sortTimestamp: event.created_at,
      actor: event.actor?.name ?? event.actor?.email ?? '',
      content: [
        {
          label: 'Event',
          richText: RailsUtility.convertFromCasedEnumFormat(event.action_type),
        },
        {
          label: 'Description',
          richText: RailsUtility.convertFromCasedEnumFormat(event.action_name),
        },
      ],
    };
  };

  type BuildActivitiesArgs = {
    rawActivities: any;
    applications: any[];
    contact: any;
    getContactDetails: () => void;
  };

  const pipelineEntryStages = ['applied', 'unlicensed', 'unlicensed_with_coursework_started'];

  const omitFirstEntryStageChange = (entityEvents: any[]): any[] => {
    const entryStageEvents = entityEvents.filter((event: any) =>
      pipelineEntryStages.some(stage => event.action_name?.endsWith(stage))
    );
    if (entryStageEvents.length === 0) return entityEvents;

    const earliest = entryStageEvents.reduce((oldest, event) =>
      event.created_at < oldest.created_at ? event : oldest
    );
    return entityEvents.filter((event: any) => event !== earliest);
  };

  const buildActivities = ({ rawActivities, applications, contact, getContactDetails }: BuildActivitiesArgs) => {
    const activities: ActivityItem[] = [];

    const meetings = rawActivities.contact_events.map((event: any) => transformEvent(event, contact));
    const emails = rawActivities.contact_emails.map(transformEmail);
    const messages = rawActivities.contact_messages.map((msg: any) => transformMessage(msg, contact));
    const notes = rawActivities.contact_notes.map((activity: any) => transformNote(activity, getContactDetails));
    const phoneCalls = rawActivities.in_app_phone_calls.map(transformInAppPhoneCall);
    const applicationActivities = (applications ?? []).map(transformApplication);
    const hasApplication = applicationActivities.some(Boolean);
    const visibleEntityEvents = hasApplication
      ? omitFirstEntryStageChange(rawActivities.entity_events)
      : rawActivities.entity_events;
    const timeline = visibleEntityEvents.map(transformEntityEvents);
    const workflowScheduledActions = rawActivities.upcoming_workflow_scheduled_actions.map(
      transformWorkflowScheduledAction
    );
    const scheduledActions = rawActivities.scheduled_actions.map(transformScheduledAction);

    const aggregation: ActivityItem[] = [
      ...applicationActivities,
      ...meetings,
      ...emails,
      ...messages,
      ...notes,
      ...phoneCalls,
      ...workflowScheduledActions,
      ...scheduledActions,
      ...timeline,
    ].filter(x => !!x);

    activities.push(...aggregation);

    const pastActivities = activities.filter(item => new Date(item.sortTimestamp) < new Date());
    const futureActivities = activities.filter(item => new Date(item.sortTimestamp) > new Date());

    pastActivities.sort((a, b) => {
      return new Date(b.sortTimestamp).getTime() - new Date(a.sortTimestamp).getTime();
    });
    futureActivities.sort((a, b) => {
      return new Date(a.sortTimestamp).getTime() - new Date(b.sortTimestamp).getTime();
    });

    return {
      past: pastActivities,
      future: futureActivities,
    };
  };

  return { buildActivities };
};
